import { useMemo } from 'react';
import { T } from 'gt-react';
import { EnvelopeIcon } from '@phosphor-icons/react';
import type { ArInvoiceInboundEmail, ArOutboundEmail } from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import { EmailThreadViewer } from '@/components/email-viewer/EmailThreadViewer';
import { EmailViewerV2 } from '@/components/email-viewer/email-viewer-v2';
import { inboundEmailToEmailData } from '@/components/email-viewer/utils';

interface ARInvoiceEmailsProps {
  inboundEmails: ArInvoiceInboundEmail[];
  outboundEmails: ArOutboundEmail[];
}

function PendingRemittanceBadge() {
  return (
    <Badge variant="secondary" className="text-xs font-normal">
      <T context="badge indicating this email's link to the invoice is from an unconfirmed remittance claim">
        Pending remittance
      </T>
    </Badge>
  );
}

export function ARInvoiceEmails({
  inboundEmails,
  outboundEmails,
}: ARInvoiceEmailsProps) {
  // Split into conversation threads vs standalone emails. Track which threads
  // contain at least one pending-remittance email so we can flag them in the UI.
  const { conversationIds, standaloneEmails, pendingConversationIds } =
    useMemo(() => {
      const ids = new Set<string>();
      const pending = new Set<string>();
      const standalone: ArInvoiceInboundEmail[] = [];
      for (const email of inboundEmails) {
        if (email.conversationId) {
          ids.add(email.conversationId);
          if (email.pendingRemittance) pending.add(email.conversationId);
        } else {
          standalone.push(email);
        }
      }
      return {
        conversationIds: [...ids],
        standaloneEmails: standalone,
        pendingConversationIds: pending,
      };
    }, [inboundEmails]);

  const hasEmails =
    conversationIds.length > 0 ||
    standaloneEmails.length > 0 ||
    outboundEmails.length > 0;

  if (!hasEmails) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <T>No emails</T>
      </div>
    );
  }

  // Single thread, no standalone, no outbound — render full-height (unless the
  // thread is from a pending remittance, in which case we need the badge header).
  if (
    conversationIds.length === 1 &&
    standaloneEmails.length === 0 &&
    outboundEmails.length === 0 &&
    !pendingConversationIds.has(conversationIds[0])
  ) {
    return <EmailThreadViewer conversationId={conversationIds[0]} />;
  }

  // Multiple threads and/or standalone emails — stack with dividers
  return (
    <div className="space-y-4 p-3">
      {outboundEmails.map((email) => (
        <div key={email.id} className="border rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <EnvelopeIcon className="h-3.5 w-3.5" />
            <T context="label for a sent invoice email entry">Sent Invoice</T>
          </div>
          <div className="text-sm font-medium">{email.subject}</div>
          <div className="space-y-0.5 text-xs text-muted-foreground">
            <div>
              <span className="font-medium">
                <T context="email sender label">From:</T>
              </span>{' '}
              {email.from}
            </div>
            <div>
              <span className="font-medium">
                <T context="email recipient label">To:</T>
              </span>{' '}
              {email.to}
            </div>
            {email.cc && (
              <div>
                <span className="font-medium">
                  <T context="email CC label">CC:</T>
                </span>{' '}
                {email.cc}
              </div>
            )}
            {email.sentAt && (
              <div>
                <span className="font-medium">
                  <T context="email sent date label">Date:</T>
                </span>{' '}
                {new Date(email.sentAt).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      ))}
      {conversationIds.map((convId) => (
        <div key={convId} className="border rounded-lg overflow-hidden p-4">
          {pendingConversationIds.has(convId) && (
            <div className="mb-2">
              <PendingRemittanceBadge />
            </div>
          )}
          <EmailThreadViewer conversationId={convId} />
        </div>
      ))}
      {standaloneEmails.map((email) => (
        <div key={email.id} className="border rounded-lg overflow-hidden p-4">
          {email.pendingRemittance && (
            <div className="mb-2">
              <PendingRemittanceBadge />
            </div>
          )}
          <EmailViewerV2 emailData={inboundEmailToEmailData(email)} />
        </div>
      ))}
    </div>
  );
}
